import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  MapPinIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { GoogleMap, Polyline, Marker, Circle, Polygon, useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { Tooltip } from '../../components/ui/Tooltip';
import DateTimePicker from '../../components/DateTimePicker';

type SearchType = 'pro' | 'unit';

interface LocationPoint {
  latitude: number;
  longitude: number;
  time: string;
  heading?: number;
  speed?: number;
}

interface LegOption {
  legNum: number;
  unitNumber: string;
  shipmentDate: string | null;
  originName: string;
  destName: string;
}

interface UnitLegOption extends LegOption {
  proNumber: string;
  origArrivalDatetime: string | null;
  origDepartDatetime: string | null;
  arrivalDatetime: string | null;
  departDatetime: string | null;
}

interface GeofenceData {
  name: string | null;
  coordinates: string | null;
  geofenceType: string | null;
  geofenceRadius: number | null;
}

interface TravelHistoryResponse {
  success: boolean;
  proNumber: string;
  legNum: number;
  unitNumber: string;
  samsaraVehicleId: string;
  shipmentDate: string;
  startTime: string;
  endTime: string;
  locationCount: number;
  locations: LocationPoint[];
  origArrivalDatetime: string | null;
  origDepartDatetime: string | null;
  arrivalDatetime: string | null;
  departDatetime: string | null;
  origGeoArrivalDatetime: string | null;
  origGeoDepartDatetime: string | null;
  destGeoArrivalDatetime: string | null;
  destGeoDepartDatetime: string | null;
  dispatchStartedDatetime: string | null;
  origin: GeofenceData;
  destination: GeofenceData;
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 29.7604, lng: -95.3698 }; // Houston

const formatTimestamp = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/Chicago',
  });
};

// Downsample points for displaying dots
const getDisplayPoints = (locations: LocationPoint[], maxPoints: number): LocationPoint[] => {
  if (locations.length <= maxPoints) return locations;

  const interval = Math.ceil(locations.length / maxPoints);
  const result: LocationPoint[] = [];

  for (let i = 0; i < locations.length; i += interval) {
    result.push(locations[i]);
  }

  // Always include the last point
  if (result[result.length - 1] !== locations[locations.length - 1]) {
    result.push(locations[locations.length - 1]);
  }

  return result;
};

// Parse coordinates string into lat/lng array
// Format: "lat,lng" for single point or "lat1,lng1;lat2,lng2;lat3,lng3" for polygon
const parseCoordinates = (coordStr: string | null): { lat: number; lng: number }[] | null => {
  if (!coordStr) return null;
  try {
    if (coordStr.includes(';')) {
      // Polygon format
      return coordStr.split(';').map(pair => {
        const [lat, lng] = pair.split(',').map(Number);
        return { lat, lng };
      });
    } else {
      // Single point format
      const [lat, lng] = coordStr.split(',').map(Number);
      return [{ lat, lng }];
    }
  } catch (e) {
    console.error('Error parsing coordinates:', coordStr, e);
    return null;
  }
};

// Get list of missing geofence events for a leg
const getMissingGeofenceEvents = (leg: {
  origArrivalDatetime: string | null;
  origDepartDatetime: string | null;
  arrivalDatetime: string | null;
  departDatetime: string | null;
}): string[] => {
  const missing: string[] = [];
  if (!leg.origArrivalDatetime) missing.push('Origin Arrive');
  if (!leg.origDepartDatetime) missing.push('Origin Depart');
  if (!leg.arrivalDatetime) missing.push('Dest Arrive');
  if (!leg.departDatetime) missing.push('Dest Depart');
  return missing;
};

// Find the index of the GPS point closest to a given datetime string
const findClosestPointIndex = (
  locations: LocationPoint[],
  targetDatetime: string | null
): number | null => {
  if (!targetDatetime || locations.length === 0) return null;

  const targetTime = new Date(targetDatetime).getTime();
  if (isNaN(targetTime)) return null;

  let closestIndex = 0;
  let closestDiff = Math.abs(new Date(locations[0].time).getTime() - targetTime);

  for (let i = 1; i < locations.length; i++) {
    const diff = Math.abs(new Date(locations[i].time).getTime() - targetTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = i;
    }
  }

  return closestIndex;
};

const DriverTravelHistory: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchType, setSearchType] = useState<SearchType>('pro');
  const [proNumber, setProNumber] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [searchDate, setSearchDate] = useState<Date | null>(null);
  const [legs, setLegs] = useState<LegOption[]>([]);
  const [unitLegs, setUnitLegs] = useState<UnitLegOption[]>([]);
  const [selectedLegNum, setSelectedLegNum] = useState<number | null>(null);
  const [selectedProNumber, setSelectedProNumber] = useState<string | null>(null);
  const [loadingLegs, setLoadingLegs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TravelHistoryResponse | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<LocationPoint | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<{ point: LocationPoint; x: number; y: number } | null>(null);
  const [dotDensity, setDotDensity] = useState(100); // Number of dots to display
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  const { isLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // Auto-search from URL query params (e.g. ?pro=123456&legnum=1)
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (didAutoSearch.current) return;
    const urlPro = searchParams.get('pro');
    const urlLeg = searchParams.get('legnum');
    if (urlPro) {
      didAutoSearch.current = true;
      setProNumber(urlPro);
      setSearchType('pro');
      // Clear params from URL to avoid re-triggering on navigation
      setSearchParams({}, { replace: true });
      if (urlLeg) {
        // Direct fetch when both pro and leg are provided
        const legNum = parseInt(urlLeg, 10);
        setSelectedLegNum(legNum);
        fetchTravelHistory(urlPro, legNum);
      } else {
        // Fetch legs and auto-select if only one
        (async () => {
          setLoadingLegs(true);
          setError(null);
          try {
            const response = await api.get(`/driver-dispatch/travel-history/${urlPro.trim()}/legs`);
            const fetchedLegs = response.data.legs || [];
            setLegs(fetchedLegs);
            if (fetchedLegs.length === 1) {
              setSelectedLegNum(fetchedLegs[0].legNum);
              fetchTravelHistory(urlPro.trim(), fetchedLegs[0].legNum);
            }
          } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'Failed to fetch legs');
          } finally {
            setLoadingLegs(false);
          }
        })();
      }
    }
  }, []); // Run once on mount

  // Find indices of GPS points closest to geo capture times with labels
  const geoHighlightMap = useMemo(() => {
    if (!data?.locations?.length) return new Map<number, string[]>();

    const indexToLabels = new Map<number, string[]>();
    const geoEvents: { time: string | null; label: string }[] = [
      { time: data.dispatchStartedDatetime, label: 'DS' },
      { time: data.origGeoArrivalDatetime, label: 'OA' },
      { time: data.origGeoDepartDatetime, label: 'OD' },
      { time: data.destGeoArrivalDatetime, label: 'DA' },
      { time: data.destGeoDepartDatetime, label: 'DD' },
    ];

    geoEvents.forEach(({ time, label }) => {
      const idx = findClosestPointIndex(data.locations, time);
      if (idx !== null) {
        const existing = indexToLabels.get(idx) || [];
        existing.push(label);
        indexToLabels.set(idx, existing);
      }
    });

    return indexToLabels;
  }, [data]);

  // Cleanup markers on unmount
  useEffect(() => {
    return () => {
      markersRef.current.forEach(marker => {
        marker.map = null;
      });
      markersRef.current = [];
    };
  }, []);

  // Create start/end markers when data changes
  useEffect(() => {
    if (!mapRef.current || !data?.locations?.length || !window.google?.maps?.marker?.AdvancedMarkerElement) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];

    const locations = data.locations;
    const startLoc = locations[0];
    const endLoc = locations[locations.length - 1];

    // Create start marker
    const startEl = document.createElement('div');
    startEl.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background: #16A34A; color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-bottom: 4px; white-space: nowrap;">
          START
        </div>
        <div style="width: 14px; height: 14px; background: #16A34A; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>
      </div>
    `;
    const startMarker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: startLoc.latitude, lng: startLoc.longitude },
      map: mapRef.current,
      content: startEl,
      title: `Start: ${formatTimestamp(startLoc.time)} CST`,
      zIndex: 1000,
    });
    markersRef.current.push(startMarker);

    // Create end marker
    const endEl = document.createElement('div');
    endEl.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background: #DC2626; color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-bottom: 4px; white-space: nowrap;">
          END
        </div>
        <div style="width: 14px; height: 14px; background: #DC2626; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>
      </div>
    `;
    const endMarker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: endLoc.latitude, lng: endLoc.longitude },
      map: mapRef.current,
      content: endEl,
      title: `End: ${formatTimestamp(endLoc.time)} CST`,
      zIndex: 1000,
    });
    markersRef.current.push(endMarker);
  }, [data]);

  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSearchPro = async () => {
    if (!proNumber.trim()) {
      setError('Please enter a PRO number');
      return;
    }

    setLoadingLegs(true);
    setError(null);
    setLegs([]);
    setUnitLegs([]);
    setSelectedLegNum(null);
    setSelectedProNumber(null);
    setData(null);
    setSelectedPoint(null);
    setHoveredPoint(null);

    try {
      const response = await api.get(`/driver-dispatch/travel-history/${proNumber.trim()}/legs`);
      const fetchedLegs = response.data.legs || [];
      setLegs(fetchedLegs);

      // Auto-select if only one leg
      if (fetchedLegs.length === 1) {
        setSelectedLegNum(fetchedLegs[0].legNum);
        fetchTravelHistory(proNumber.trim(), fetchedLegs[0].legNum);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch legs');
    } finally {
      setLoadingLegs(false);
    }
  };

  const handleSearchUnit = async () => {
    if (!unitNumber.trim()) {
      setError('Please enter a unit number');
      return;
    }
    if (!searchDate) {
      setError('Please select a date');
      return;
    }

    setLoadingLegs(true);
    setError(null);
    setLegs([]);
    setUnitLegs([]);
    setSelectedLegNum(null);
    setSelectedProNumber(null);
    setData(null);
    setSelectedPoint(null);
    setHoveredPoint(null);

    try {
      const dateStr = formatDateForApi(searchDate);
      const response = await api.get(`/driver-dispatch/travel-history/by-unit/${unitNumber.trim()}/legs`, {
        params: { date: dateStr }
      });
      const fetchedLegs = response.data.legs || [];
      setUnitLegs(fetchedLegs);

      if (fetchedLegs.length === 0) {
        setError(`No legs found for unit ${unitNumber.trim()} on ${dateStr}`);
      } else if (fetchedLegs.length === 1) {
        // Auto-select if only one leg
        setSelectedProNumber(fetchedLegs[0].proNumber);
        setSelectedLegNum(fetchedLegs[0].legNum);
        fetchTravelHistory(fetchedLegs[0].proNumber, fetchedLegs[0].legNum);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch legs');
    } finally {
      setLoadingLegs(false);
    }
  };

  const fetchTravelHistory = async (pro: string, legNum: number) => {
    setLoading(true);
    setError(null);
    setData(null);
    setSelectedPoint(null);
    setHoveredPoint(null);

    const proNum = String(pro || '').trim();
    if (!proNum) {
      setError('Invalid PRO number');
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/driver-dispatch/travel-history/${proNum}`, {
        params: { legnum: legNum }
      });
      setData(response.data);

      // Fit map to bounds after data loads
      if (response.data.locations?.length > 0) {
        const locs = response.data.locations;
        const lats = locs.map((l: LocationPoint) => l.latitude);
        const lngs = locs.map((l: LocationPoint) => l.longitude);
        setMapCenter({
          lat: (Math.min(...lats) + Math.max(...lats)) / 2,
          lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
        });

        if (mapRef.current) {
          const bounds = new google.maps.LatLngBounds();
          locs.forEach((loc: LocationPoint) => {
            bounds.extend({ lat: loc.latitude, lng: loc.longitude });
          });
          mapRef.current.fitBounds(bounds, 50);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch travel history');
    } finally {
      setLoading(false);
    }
  };

  const handleLegChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const legNum = parseInt(e.target.value, 10);
    setSelectedLegNum(legNum);
    fetchTravelHistory(proNumber.trim(), legNum);
  };

  const handleUnitLegSelect = (pro: string, legNum: number) => {
    setSelectedProNumber(pro);
    setSelectedLegNum(legNum);
    fetchTravelHistory(pro, legNum);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (searchType === 'pro') {
        handleSearchPro();
      } else {
        handleSearchUnit();
      }
    }
  };

  const handleSearch = () => {
    if (searchType === 'pro') {
      handleSearchPro();
    } else {
      handleSearchUnit();
    }
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);


  const handleRowClick = (point: LocationPoint) => {
    setSelectedPoint(point);
    if (mapRef.current) {
      mapRef.current.panTo({ lat: point.latitude, lng: point.longitude });
      mapRef.current.setZoom(16);
    }
  };

  const handleDotMouseOver = (point: LocationPoint, e: google.maps.MapMouseEvent) => {
    if (e.domEvent && mapRef.current) {
      const mouseEvent = e.domEvent as MouseEvent;
      setHoveredPoint({
        point,
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
      });
    }
  };

  const handleDotMouseOut = () => {
    setHoveredPoint(null);
  };

  const polylinePath = data?.locations?.map(loc => ({
    lat: loc.latitude,
    lng: loc.longitude,
  })) || [];

  const displayPoints = data?.locations ? getDisplayPoints(data.locations, dotDensity) : [];

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Search Input */}
      <div className="mb-4 flex gap-4 items-end flex-wrap">
        {/* Search Type Dropdown */}
        <div className="min-w-[120px]">
          <label htmlFor="searchType" className="block text-sm font-medium text-label mb-1">
            Search By
          </label>
          <select
            id="searchType"
            value={searchType}
            onChange={(e) => {
              setSearchType(e.target.value as SearchType);
              setError(null);
              setLegs([]);
              setUnitLegs([]);
              setData(null);
              setSelectedLegNum(null);
              setSelectedProNumber(null);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="pro">Pro</option>
            <option value="unit">Driver</option>
          </select>
        </div>

        {/* PRO Number Input (when searching by PRO) */}
        {searchType === 'pro' && (
          <div className="flex-1 min-w-[200px] max-w-md">
            <label htmlFor="proNumber" className="block text-sm font-medium text-label mb-1">
              PRO Number
            </label>
            <input
              type="text"
              id="proNumber"
              value={proNumber}
              onChange={(e) => setProNumber(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter PRO number"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Unit Number + Date (when searching by Unit) */}
        {searchType === 'unit' && (
          <>
            <div className="min-w-[150px]">
              <label htmlFor="unitNumber" className="block text-sm font-medium text-label mb-1">
                Unit Number
              </label>
              <input
                type="text"
                id="unitNumber"
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter unit #"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-label mb-1">
                Date
              </label>
              <DateTimePicker
                mode="date"
                selectionType="single"
                value={searchDate}
                onChange={(val) => setSearchDate(val as Date | null)}
                placeholder="Select date"
                autoApply={true}
                labelType="none"
              />
            </div>
          </>
        )}

        <button
          onClick={handleSearch}
          disabled={loadingLegs || loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loadingLegs ? (
            <ArrowPathIcon className="h-5 w-5 animate-spin" />
          ) : (
            <MagnifyingGlassIcon className="h-5 w-5" />
          )}
          Search
        </button>

        {/* Leg Dropdown (for PRO search with multiple legs) */}
        {searchType === 'pro' && legs.length > 1 && (
          <div className="min-w-[300px]">
            <label htmlFor="legSelect" className="block text-sm font-medium text-label mb-1">
              Select Leg
            </label>
            <select
              id="legSelect"
              value={selectedLegNum || ''}
              onChange={handleLegChange}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select a leg --</option>
              {legs.map((leg) => (
                <option key={leg.legNum} value={leg.legNum}>
                  Leg {leg.legNum}: {leg.unitNumber} - {leg.originName} → {leg.destName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Results Info */}
      {data && (
        <div className="mb-4 p-4 bg-gray-50 border border-rule rounded-lg dark:bg-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MapPinIcon className="h-5 w-5 text-gray-500" />
              <span className="font-medium">PRO: {data.proNumber} - Leg {data.legNum}</span>
            </div>
            {/* Dot Density Slider */}
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 whitespace-nowrap">
                Dots: {displayPoints.length}
              </label>
              <input
                type="range"
                min="25"
                max="500"
                step="25"
                value={dotDensity}
                onChange={(e) => setDotDensity(parseInt(e.target.value, 10))}
                className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm text-gray-600 mb-3">
            <div>
              <span className="font-medium">Unit:</span> {data.unitNumber}
            </div>
            <div>
              <span className="font-medium">Date:</span> {data.shipmentDate}
            </div>
            <div>
              <span className="font-medium">Dispatch Started:</span> {data.dispatchStartedDatetime || '-'}
            </div>
            <div>
              <span className="font-medium">GPS Points:</span> {data.locationCount.toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Vehicle ID:</span> {data.samsaraVehicleId}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <span className="font-medium">Orig Arrive:</span> {data.origArrivalDatetime || '-'}
              {!data.origArrivalDatetime && (
                <Tooltip content="Driver did not enter origin geofence">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Orig Depart:</span> {data.origDepartDatetime || '-'}
              {!data.origDepartDatetime && (
                <Tooltip content="Driver did not exit origin geofence">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Dest Arrive:</span> {data.arrivalDatetime || '-'}
              {!data.arrivalDatetime && (
                <Tooltip content="Driver did not enter destination geofence">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Dest Depart:</span> {data.departDatetime || '-'}
              {!data.departDatetime && (
                <Tooltip content="Driver did not exit destination geofence">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 mt-1">
            <div className="flex items-center gap-1">
              <span className="font-medium">Geo Capture:</span> {data.origGeoArrivalDatetime || '-'}
              {!data.origGeoArrivalDatetime && (
                <Tooltip content="No geofence entry captured at origin">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Geo Capture:</span> {data.origGeoDepartDatetime || '-'}
              {!data.origGeoDepartDatetime && (
                <Tooltip content="No geofence exit captured at origin">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Geo Capture:</span> {data.destGeoArrivalDatetime || '-'}
              {!data.destGeoArrivalDatetime && (
                <Tooltip content="No geofence entry captured at destination">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">Geo Capture:</span> {data.destGeoDepartDatetime || '-'}
              {!data.destGeoDepartDatetime && (
                <Tooltip content="No geofence exit captured at destination">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Map and Table Layout */}
      {(data || (searchType === 'unit' && unitLegs.length > 0)) && (
        <div className={`grid grid-cols-1 gap-4 flex-1 min-h-0 ${searchType === 'unit' && unitLegs.length > 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          {/* Pro/Leg List (for unit search) */}
          {searchType === 'unit' && unitLegs.length > 0 && (
            <div className="lg:col-span-1 flex flex-col min-h-0">
              <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <span className="font-medium text-label text-sm">
                    Unit {unitNumber} - {searchDate ? formatDateForApi(searchDate) : ''}
                  </span>
                </div>
                <div className="divide-y divide-gray-200 overflow-y-auto flex-1">
                  {unitLegs.map((leg) => {
                    const missingEvents = getMissingGeofenceEvents(leg);
                    return (
                      <div
                        key={`${leg.proNumber}-${leg.legNum}`}
                        onClick={() => handleUnitLegSelect(leg.proNumber, leg.legNum)}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                          selectedProNumber === leg.proNumber && selectedLegNum === leg.legNum ? 'bg-blue-100' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-blue-600 text-sm">{leg.proNumber} - Leg {leg.legNum}</span>
                          {missingEvents.length > 0 && (
                            <Tooltip content={`Missing: ${missingEvents.join(', ')}`}>
                              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                            </Tooltip>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{leg.originName} → {leg.destName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Map */}
          <div className={`${searchType === 'unit' && unitLegs.length > 0 ? 'lg:col-span-2' : 'lg:col-span-2'} relative flex flex-col min-h-0`}>
            {!data && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 flex items-center justify-center flex-1">
                <p className="text-gray-500">Select a leg to view travel history</p>
              </div>
            )}
            {data && isLoaded && (
              <div className="border border-gray-200 rounded-lg overflow-hidden flex-1">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={10}
                  onLoad={onMapLoad}
                  options={{
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    mapId: 'driver-travel-history-map',
                  }}
                >
                  {polylinePath.length > 0 && (
                    <Polyline
                      path={polylinePath}
                      options={{
                        strokeColor: '#2563EB',
                        strokeOpacity: 0.8,
                        strokeWeight: 3,
                      }}
                    />
                  )}

                  {/* GPS point dots */}
                  {displayPoints.map((point, index) => (
                    <Marker
                      key={index}
                      position={{ lat: point.latitude, lng: point.longitude }}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 4,
                        fillColor: '#3B82F6',
                        fillOpacity: 0.8,
                        strokeColor: '#1D4ED8',
                        strokeWeight: 1,
                      }}
                      zIndex={100}
                      onMouseOver={(e) => handleDotMouseOver(point, e)}
                      onMouseOut={handleDotMouseOut}
                      onClick={() => handleRowClick(point)}
                    />
                  ))}

                  {selectedPoint && (
                    <Marker
                      position={{ lat: selectedPoint.latitude, lng: selectedPoint.longitude }}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: '#F59E0B',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 2,
                      }}
                      zIndex={500}
                    />
                  )}

                  {/* Origin Geofence */}
                  {(() => {
                    const originCoords = parseCoordinates(data.origin?.coordinates);
                    if (!originCoords) return null;

                    const isCircular = data.origin?.geofenceType?.toUpperCase() === 'CIRCULAR';
                    const radiusMeters = (data.origin?.geofenceRadius || 0.5) * 1609.34;

                    return (
                      <>
                        {isCircular ? (
                          <Circle
                            center={originCoords[0]}
                            radius={radiusMeters}
                            options={{
                              fillColor: '#10B981',
                              fillOpacity: 0.2,
                              strokeColor: '#10B981',
                              strokeWeight: 2,
                              zIndex: 50,
                            }}
                          />
                        ) : (
                          <Polygon
                            paths={originCoords}
                            options={{
                              fillColor: '#10B981',
                              fillOpacity: 0.2,
                              strokeColor: '#10B981',
                              strokeWeight: 2,
                              zIndex: 50,
                            }}
                          />
                        )}
                        <Marker
                          position={originCoords[0]}
                          label={{
                            text: 'O',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                          icon={{
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 12,
                            fillColor: '#10B981',
                            fillOpacity: 1,
                            strokeColor: '#FFFFFF',
                            strokeWeight: 2,
                          }}
                          zIndex={200}
                        />
                      </>
                    );
                  })()}

                  {/* Destination Geofence */}
                  {(() => {
                    const destCoords = parseCoordinates(data.destination?.coordinates);
                    if (!destCoords) return null;

                    const isCircular = data.destination?.geofenceType?.toUpperCase() === 'CIRCULAR';
                    const radiusMeters = (data.destination?.geofenceRadius || 0.5) * 1609.34;

                    return (
                      <>
                        {isCircular ? (
                          <Circle
                            center={destCoords[0]}
                            radius={radiusMeters}
                            options={{
                              fillColor: '#3B82F6',
                              fillOpacity: 0.2,
                              strokeColor: '#3B82F6',
                              strokeWeight: 2,
                              zIndex: 50,
                            }}
                          />
                        ) : (
                          <Polygon
                            paths={destCoords}
                            options={{
                              fillColor: '#3B82F6',
                              fillOpacity: 0.2,
                              strokeColor: '#3B82F6',
                              strokeWeight: 2,
                              zIndex: 50,
                            }}
                          />
                        )}
                        <Marker
                          position={destCoords[0]}
                          label={{
                            text: 'D',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                          icon={{
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 12,
                            fillColor: '#3B82F6',
                            fillOpacity: 1,
                            strokeColor: '#FFFFFF',
                            strokeWeight: 2,
                          }}
                          zIndex={200}
                        />
                      </>
                    );
                  })()}
                </GoogleMap>
              </div>
            )}

            {/* Hover Tooltip - portaled to body to escape modal/drawer stacking context */}
            {hoveredPoint && createPortal(
              <div
                className="fixed z-[9999] bg-gray-900 text-white text-sm rounded-lg px-3 py-2 shadow-lg pointer-events-none"
                style={{
                  left: hoveredPoint.x + 10,
                  top: hoveredPoint.y - 40,
                }}
              >
                <div className="font-medium">{formatTimestamp(hoveredPoint.point.time)} CST</div>
                <div className="text-gray-300">
                  Speed: {hoveredPoint.point.speed !== undefined ? `${Math.round(hoveredPoint.point.speed)} mph` : 'N/A'}
                </div>
              </div>,
              document.body
            )}
          </div>

          {/* Points Table */}
          {data && (
            <div className="lg:col-span-1 flex flex-col min-h-0">
              <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-gray-500" />
                    <span className="font-medium text-label">GPS Points</span>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time (CST)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Speed</th>
                      </tr>
                    </thead>
                    <tbody className="bg-panel divide-y divide-gray-200">
                      {data.locations.map((point, index) => {
                        const geoLabels = geoHighlightMap.get(index);
                        const isGeoHighlight = !!geoLabels;
                        return (
                        <tr
                          key={index}
                          onClick={() => handleRowClick(point)}
                          className={`cursor-pointer hover:bg-blue-50 ${
                            selectedPoint === point ? 'bg-blue-100' : ''
                          } ${index === 0 ? 'bg-green-50' : ''} ${
                            index === data.locations.length - 1 ? 'bg-red-50' : ''
                          } ${isGeoHighlight ? 'bg-amber-50' : ''}`}
                        >
                          <td className={`px-3 py-2 text-sm text-gray-500 ${isGeoHighlight ? 'font-bold' : ''}`}>{index + 1}</td>
                          <td className={`px-3 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap ${isGeoHighlight ? 'font-bold' : ''}`}>
                            <span className="flex items-center gap-1">
                              {formatTimestamp(point.time)}
                              {geoLabels?.map(label => {
                                const badgeColor = label === 'DS'
                                  ? 'bg-purple-100 text-purple-700'
                                  : label.startsWith('O')
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-blue-100 text-blue-700';
                                return (
                                  <span
                                    key={label}
                                    className={`text-xs px-1.5 py-0.5 rounded font-medium ${badgeColor}`}
                                  >
                                    {label}
                                  </span>
                                );
                              })}
                            </span>
                          </td>
                          <td className={`px-3 py-2 text-sm text-gray-500 ${isGeoHighlight ? 'font-bold' : ''}`}>
                            {point.speed !== undefined ? `${Math.round(point.speed)} mph` : '-'}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading travel history...</span>
        </div>
      )}
    </div>
  );
};

export default DriverTravelHistory;
